import { ConsoleClient, EmailClient, MonitoringClient, OpsClient, PlatformClient, PublicClient, type ConsoleApi, type EmailApi, type LiveSubscription, type MonitoringApi, type OpsApi, type PlatformConsoleApi, type PublicApi } from '@takeit4me/sdk'
import { ENV } from './lib/env'
import { emitUnauthorized, getAccessToken } from './lib/supabase'
import { withCompanyHeader } from './lib/company'
import { inspectForbidden } from './lib/authPolicy'
import { opsToken } from './lib/opsAuth'

export const MOCK = ENV.mock

/**
 * Every console API call: the chosen company travels as `x-takeit4me-company` (X-04); a 401 signs out;
 * a 403 for the sign-in policy or a suspended account is reported to the auth provider (lib/authPolicy).
 */
export const consoleFetch: typeof fetch = async (input, init) => {
  const res = await fetch(input, withCompanyHeader(init))
  if (res.status === 401) emitUnauthorized()
  else if (res.status === 403) await inspectForbidden(res)
  return res
}

/**
 * Mocks live in their own chunks, fetched only in mock mode, so demo data never ships in the real
 * console bundle. Every method forwards to the instance once it has loaded.
 */
function lazy<T extends object>(load: () => Promise<T>, live = false): T {
  let inst: Promise<T> | null = null
  const get = () => (inst ??= load())
  return new Proxy({} as T, {
    get(_t, prop: string) {
      if (prop === 'then') return undefined // not a thenable
      if (live && prop === 'subscribeLive') {
        return (...args: Parameters<ConsoleApi['subscribeLive']>): LiveSubscription => {
          let sub: LiveSubscription | null = null
          let closed = false
          void get().then((m) => { if (!closed) sub = (m as unknown as ConsoleApi).subscribeLive(...args) })
          return { close() { closed = true; sub?.close() } }
        }
      }
      return (...args: unknown[]) => get().then((m) => (m as unknown as Record<string, (...a: unknown[]) => unknown>)[prop](...args))
    },
  })
}

/** The one API client for the app: real HTTP, or the in-browser mock when VITE_MOCK=1. */
export const api: ConsoleApi = MOCK
  ? lazy(() => import('./mock/MockConsoleApi').then((m) => new m.MockConsoleApi()), true)
  : new ConsoleClient({ baseUrl: ENV.apiBase, token: getAccessToken, fetch: consoleFetch })

/** Lifecycle, onboarding, billing, sign-in policy, invites and the audit log (`/api/v1`). */
export const platform: PlatformConsoleApi = MOCK
  ? lazy(() => import('./mock/MockPlatform').then((m) => m.mockPlatform()))
  : new PlatformClient({ baseUrl: ENV.apiBase, token: getAccessToken, fetch: consoleFetch })

/** Sign-up and login options: no sign-in. */
export const publicApi: PublicApi = MOCK
  ? lazy(() => import('./mock/MockPlatform').then((m) => m.mockPublic()))
  : new PublicClient({ baseUrl: ENV.publicApiBase })

/** The operator console (`/ops/v1`): Cloudflare Access in production, a dev token locally. */
export const ops: OpsApi = MOCK
  ? lazy(() => import('./mock/MockPlatform').then((m) => m.mockOps()))
  : new OpsClient({ baseUrl: ENV.apiBase, token: opsToken })

/** Monitoring a company acts on: outcomes, alarm history, failed intake in bulk, dead letters, coverage (`/api/v1`). */
export const monitoring: MonitoringApi = MOCK
  ? lazy(() => import('./mock/MockMonitoring').then((m) => new m.MockMonitoringApi()))
  : new MonitoringClient({ baseUrl: ENV.apiBase, token: getAccessToken, fetch: consoleFetch })

export { ApiError } from '@takeit4me/sdk'

/** Email and the review desk (mailboxes, filtered mail, review actions, report): same pattern, its own mock chunk. */
function lazyEmailMock(): EmailApi {
  let inst: Promise<EmailApi> | null = null
  const load = () => (inst ??= import('./mock/MockEmailApi').then((m) => new m.MockEmailApi()))
  return new Proxy({} as EmailApi, {
    get(_t, prop: string) {
      if (prop === 'then') return undefined
      return (...args: unknown[]) => load().then((m) => (m as unknown as Record<string, (...a: unknown[]) => unknown>)[prop](...args))
    },
  })
}
export const emailApi: EmailApi = MOCK ? lazyEmailMock() : new EmailClient({ baseUrl: ENV.apiBase, token: getAccessToken, fetch: consoleFetch })
